/**
 * ALICE Remediation Posture widget.
 *
 * Replaces the old "Fix availability by severity" chart. Answers the
 * question the user actually cares about: for every vulnerable install
 * in the estate, WILL ALICE resolve it? Broken into four outcomes:
 *
 *   auto_resolving  — packaged in ALICE AND auto-update is on. Next
 *                     release rolls the fix. No user action required.
 *   stale_package   — packaged in ALICE BUT auto-update off (or the
 *                     package version has fallen behind). New releases
 *                     won't be pushed automatically — user must flip
 *                     auto-update on or supersede the package.
 *   unmanaged       — vulnerable and mapped, but no ALICE package.
 *                     Remediation only happens once packaging is done.
 *   unmapped        — family has no CPE, so ALICE can't determine
 *                     vulnerability. Hidden risk — bulk-map action.
 */

import React from "react";
import { StatusPill } from "@/components/ui/status-pill";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell, LabelList,
} from "recharts";
import { ShieldCheck, AlertTriangle, PackagePlus, HelpCircle } from "lucide-react";

const POSTURE_META = {
  auto_resolving: {
    label: "Auto-resolving",
    color: "#10b981", // emerald-500
    ink: "#8AF0B4", // text on the gradient or dark canvas (Status Pill palette)
    pill: "healthy",
    icon: ShieldCheck,
    blurb: "Packaged with auto-update on — next release will remediate.",
    tone: "text-emerald-300",
  },
  stale_package: {
    label: "Stale package",
    color: "#f59e0b", // amber-500
    ink: "#F7CE7C",
    pill: "attention",
    icon: AlertTriangle,
    blurb: "Auto-update disabled or lagging — action required.",
    tone: "text-amber-300",
  },
  unmanaged: {
    label: "Unmanaged",
    color: "#dc2626", // red-600
    ink: "#FFB1A6",
    pill: "risk",
    icon: PackagePlus,
    blurb: "No ALICE package — package it or accept the risk.",
    tone: "text-red-300",
  },
  unmapped: {
    label: "Family not mapped",
    color: "#6b7280", // gray-500
    ink: "#B4BBCB",
    pill: "onboarding",
    icon: HelpCircle,
    blurb: "No CPE mapped — hidden risk. Map to reveal exposure.",
    tone: "text-white/70",
  },
};

const POSTURE_ORDER = ["auto_resolving", "stale_package", "unmanaged", "unmapped"];

const CARD_BG =
  "bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] " +
  "border border-[rgba(229,239,250,0.1)] rounded-xl";

const TOOLTIP_STYLE = {
  contentStyle: {
    background: "rgba(15, 23, 42, 0.95)",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 8,
    color: "#fff",
    fontSize: 12,
    padding: "8px 12px",
  },
  itemStyle:  { color: "#fff" },
  labelStyle: { color: "rgba(255,255,255,0.75)", fontWeight: 600, marginBottom: 4 },
  cursor:     { fill: "rgba(255,255,255,0.06)" },
};

/**
 * Inline pill used in tables and drill-downs to tag a single row's
 * ALICE remediation posture. Kept tiny — one word + coloured dot — so
 * it fits in narrow columns without wrapping.
 */
export const PosturePill = ({ posture, size = "sm", testId }) => {
  const meta = POSTURE_META[posture] || POSTURE_META.unmapped;
  // The ALICE Status Pill (Light/Dark themes, October 2026): the earlier tinted chip drew
  // red-600 text on a red tint, which was unreadable on the Light gradient.
  return (
    <StatusPill
      tone={meta.pill}
      title={meta.blurb}
      className={size === "xs" ? "!px-[8px] !py-[2px] !text-[10px]" : undefined}
      data-testid={testId || `posture-pill-${posture}`}
    >
      {meta.label}
    </StatusPill>
  );
};

const PostureLegend = ({ rows, metric }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
    {rows.map((r) => {
      const meta = POSTURE_META[r.state] || POSTURE_META.unmapped;
      const Icon = meta.icon;
      return (
        <div
          key={r.state}
          className="flex items-start gap-2 rounded-lg p-2 bg-white/[0.03] border border-white/[0.06]"
          data-testid={`posture-legend-${r.state}`}
        >
          <div
            className="mt-0.5 shrink-0 rounded-md p-1"
            style={{ background: `${meta.color}22`, color: meta.color }}
          >
            <Icon className="w-3.5 h-3.5" />
          </div>
          <div className="min-w-0">
            <div className="flex items-baseline gap-2">
              <span className={`text-sm font-semibold ${meta.tone}`}>
                {meta.label}
              </span>
              <span className="text-sm text-white font-mono">
                {(r[metric] || 0).toLocaleString()}
              </span>
            </div>
            <div className="text-[11px] text-white/60 leading-snug">
              {meta.blurb}
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

const PostureYAxisTick = ({ x, y, payload }) => {
  const meta = POSTURE_META[payload.value] || POSTURE_META.unmapped;
  return (
    <text
      x={x - 4}
      y={y + 4}
      textAnchor="end"
      fill={meta.ink}
      fontSize={11}
      fontWeight={600}
    >
      {meta.label}
    </text>
  );
};

// families = every mapped family (live CVE or not) by its least-covered
// version; mapped_installs = their install footprint; devices = distinct
// devices with a matched CVE.
const METRIC_LABEL = { families: "Families", mapped_installs: "Installs", devices: "Devices at risk" };

const RemediationPostureWidget = ({ data, metric = "families", onMetricChange, onSelectPosture }) => {
  const rows = React.useMemo(() => {
    if (!data) return [];
    const byState = Object.fromEntries((data || []).map((r) => [r.state, r]));
    return POSTURE_ORDER
      .map((s) => byState[s] || { state: s, families: 0, mapped_installs: 0, apps: 0, installs: 0, devices: 0 });
  }, [data]);

  const total = rows.reduce((acc, r) => acc + (r[metric] || 0), 0);
  const autoRow = rows.find((r) => r.state === "auto_resolving") || {};
  const coveragePct = total > 0
    ? Math.round(((autoRow[metric] || 0) / total) * 100)
    : 0;

  const empty = total === 0;

  return (
    <div className={`${CARD_BG} p-5`} data-testid="widget-remediation-posture">
      <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-white">
            ALICE Remediation Posture
          </div>
          <div className="text-xs text-white/60 mt-0.5">
            Every mapped family — will ALICE resolve it when a CVE lands?
            {!empty && (
              <>
                {" "}
                <span className="text-emerald-300 font-semibold">
                  {coveragePct}%
                </span>{" "}
                auto-resolving coverage.
              </>
            )}
          </div>
        </div>
        <div
          className="inline-flex rounded-lg bg-white/[0.06] p-0.5 border border-white/[0.08]"
          role="tablist"
        >
          {["families", "mapped_installs", "devices"].map((m) => (
            <button
              key={m}
              onClick={() => onMetricChange && onMetricChange(m)}
              className={`text-[11px] px-2 py-1 rounded-md capitalize transition ${
                metric === m
                  ? "bg-cyan-600/70 text-white"
                  : "text-white/70 hover:text-white"
              }`}
              data-testid={`posture-metric-${m}`}
            >
              {METRIC_LABEL[m]}
            </button>
          ))}
        </div>
      </div>

      {empty ? (
        <div className="flex items-center justify-center h-[200px] text-white/40 text-sm">
          No families mapped to a CVE product yet.
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={210}>
            <BarChart
              data={rows}
              layout="vertical"
              margin={{ left: 0, right: 40, top: 4, bottom: 4 }}
            >
              <CartesianGrid horizontal={false} stroke="rgba(255,255,255,0.06)" />
              <XAxis type="number" hide />
              <YAxis
                type="category"
                dataKey="state"
                stroke="rgba(255,255,255,0.7)"
                fontSize={11}
                width={130}
                tick={PostureYAxisTick}
              />
              <Tooltip
                contentStyle={TOOLTIP_STYLE.contentStyle}
                itemStyle={TOOLTIP_STYLE.itemStyle}
                labelStyle={TOOLTIP_STYLE.labelStyle}
                cursor={TOOLTIP_STYLE.cursor}
                formatter={(v, _k, item) => {
                  const p = item.payload;
                  return [
                    `${(p[metric] || 0).toLocaleString()} ${(METRIC_LABEL[metric] || metric).toLowerCase()}` +
                      ` · ${p.apps} app${p.apps === 1 ? "" : "s"}` +
                      ` · ${p.devices.toLocaleString()} devices`,
                    POSTURE_META[p.state]?.label,
                  ];
                }}
              />
              <Bar
                dataKey={metric}
                radius={[0, 4, 4, 0]}
                onClick={(bar) => onSelectPosture && onSelectPosture(bar.state)}
                cursor={onSelectPosture ? "pointer" : "default"}
              >
                {rows.map((r) => (
                  <Cell
                    key={r.state}
                    fill={(POSTURE_META[r.state] || POSTURE_META.unmapped).color}
                  />
                ))}
                <LabelList
                  dataKey={metric}
                  position="right"
                  style={{ fill: "rgba(255,255,255,0.9)", fontSize: 11 }}
                  formatter={(v) => (v || 0).toLocaleString()}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <PostureLegend rows={rows} metric={metric} />
        </>
      )}
    </div>
  );
};

export default RemediationPostureWidget;
